import React from "react";
import { X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

function formatPrice(price) {
  if (price >= 10000000) return `Rs. ${(price / 10000000).toFixed(1)} Cr`;
  if (price >= 100000) return `Rs. ${(price / 100000).toFixed(1)} Lakh`;
  return `Rs. ${price.toLocaleString("en-IN")}`;
}

export default function PlotCompare({ plots, onClose }) {
  const { lang } = useLang();

  if (!plots || plots.length < 2) return null;

  const labels = [
    { key: "size", en: "Plot Size", hi: "\u092a\u094d\u0932\u0949\u091f \u0906\u0915\u093e\u0930" },
    { key: "price_per_sqmtr", en: "Rate / Sq.Mtr", hi: "\u0926\u0930 / \u0935\u0930\u094d\u0917 \u092e\u0940\u091f\u0930" },
    { key: "total_price", en: "Total Value", hi: "\u0915\u0941\u0932 \u092e\u0942\u0932\u094d\u092f" },
    { key: "status", en: "Status", hi: "\u0938\u094d\u0925\u093f\u0924\u093f" },
    { key: "hectare", en: "Area (Hectare)", hi: "\u0915\u094d\u0937\u0947\u0924\u094d\u0930\u092b\u0932 (\u0939\u0947\u0915\u094d\u091f\u0947\u092f\u0930)" },
    { key: "location", en: "Location", hi: "\u0938\u094d\u0925\u093e\u0928" },
    { key: "features", en: "Features", hi: "\u0935\u093f\u0936\u0947\u0937\u0924\u093e\u090f\u0902" },
  ];

  const getValue = (plot, key) => {
    switch (key) {
      case "size": return `${plot.size.toLocaleString("en-IN")} Sq.Mtr`;
      case "price_per_sqmtr": return `Rs. ${plot.price_per_sqmtr?.toLocaleString("en-IN")}`;
      case "total_price": return formatPrice(plot.total_price || plot.size * (plot.price_per_sqmtr || 0));
      case "status": return plot.status?.charAt(0).toUpperCase() + plot.status?.slice(1);
      case "hectare": return `${(plot.size / 10000).toFixed(4)} ha`;
      case "location": return "IIE Sigaddi, SIIDCUL, Kotdwar";
      case "features": return plot.features?.join(", ") || "—";
      default: return "—";
    }
  };

  const getBestValue = (key) => {
    if (key === "price_per_sqmtr") return Math.min(...plots.map(p => p.price_per_sqmtr || 0));
    if (key === "size") return Math.max(...plots.map(p => p.size || 0));
    return null;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="plot-compare-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[#1E293B] border border-white/10 w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl z-10">
        <div className="bg-slate-900 px-6 py-4 flex items-center justify-between sticky top-0 z-10 border-b border-white/10">
          <h2 className="text-white text-xl font-bold tracking-wide uppercase"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            {lang === "hi" ? "\u092a\u094d\u0932\u0949\u091f \u0924\u0941\u0932\u0928\u093e" : "Plot Comparison"}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="compare-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-800 border-b border-white/10">
                <th className="text-left px-4 py-3 text-xs font-bold text-slate-400 uppercase tracking-wide w-36">
                  {lang === "hi" ? "\u0935\u093f\u0935\u0930\u0923" : "Details"}
                </th>
                {plots.map((p) => (
                  <th key={p.id} className="text-center px-4 py-3">
                    <div className="text-white font-bold text-lg" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                      {p.size.toLocaleString("en-IN")}
                    </div>
                    <div className="text-slate-500 text-xs">SQ.MTR</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {labels.map((row, i) => {
                const best = getBestValue(row.key);
                return (
                  <tr key={row.key} className={i % 2 === 0 ? "bg-[#1E293B]" : "bg-slate-800/50"}>
                    <td className="px-4 py-3 text-sm font-semibold text-slate-300 border-r border-white/[0.06]">
                      {lang === "hi" ? row.hi : row.en}
                    </td>
                    {plots.map((p) => {
                      const val = getValue(p, row.key);
                      const isBest = (row.key === "price_per_sqmtr" && p.price_per_sqmtr === best) ||
                                     (row.key === "size" && p.size === best);
                      return (
                        <td key={p.id} className={`px-4 py-3 text-center text-sm ${
                          row.key === "status" 
                            ? p.status === "available" ? "text-emerald-400 font-bold" : "text-slate-500"
                            : isBest ? "text-orange-400 font-bold" : "text-slate-300"
                        }`}>
                          {row.key === "features" ? (
                            <div className="text-xs text-left">{val}</div>
                          ) : val}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-4 bg-orange-500/10 border-t border-orange-500/30 text-center">
          <p className="text-orange-400 text-sm font-medium">
            {lang === "hi" 
              ? "\u092d\u0941\u0917\u0924\u093e\u0928 \u0928\u0940\u0924\u093f: 10% \u092c\u0941\u0915\u093f\u0902\u0917 → 15% \u0906\u0935\u0902\u091f\u0928 (30 \u0926\u093f\u0928) → 75% \u0932\u0940\u091c \u0921\u0940\u0921 (6 \u092e\u0939\u0940\u0928\u0947)"
              : "Payment: 10% Booking → 15% Allotment (30 days) → 75% Lease Deed (6 months)"}
          </p>
        </div>
      </div>
    </div>
  );
}
